import pgrid from 'projection-grid';
import _ from 'underscore';
import { PageReady } from '@bingads-webui/page-ready';

const { version } = pgrid;

// Only log the navigation timing info for first grid data load for Grid performance measurement
// during page load.
let isInitialGridDataLoad = true;
let isPageReadyFulfilled = false;

function logMessage({ api, message, logger }) {
  logger.logMessage(['ClientPerf'], {
    name: 'Info',
    value: 0,
  }, api, message);
}

PageReady.afterPageReady().then(() => {
  isPageReadyFulfilled = true;
});

function logPerformanceInfo({
  name,
  gridView,
  virtualized,
  logNavTiming = false,
  track,
  logger,
  appConfig,
}) {
  const timeStarted = performance.now();
  const activeTimeBeforeStarted = _.result(performance, 'activeTime', NaN);
  const { id } = track;

  logger.logConsoleStamp(`Grid Start: ${name} - ${timeStarted}`);

  gridView.once('didReload', (isSuccess) => {
    if (isSuccess && id === track.id) {
      const timeFetched = performance.now();
      const activeTimeBeforeFetched = _.result(performance, 'activeTime', NaN);

      logger.logConsoleStamp(`Grid Data Fetched: ${name} - ${timeFetched}`);

      gridView.once('didRedraw', () => {
        if (id === track.id) {
          // In case the document is hidden, the redraw could be delayed by browser optimization.
          // Drop the log in this case.
          const timeRendered = performance.now();
          const activeTimeBeforeRendered = _.result(performance, 'activeTime', NaN);

          logger.logConsoleStamp(`Grid Rendered: ${name} - ${timeRendered}`);

          const landingPageUrl = appConfig.get('LandingPageUrl') || '';

          logMessage({
            api: name,
            message: JSON.stringify(_.defaults({
              Version: `projection-grid@${version}`,
              GridName: name,
              IsPageReadyFulfilled: isPageReadyFulfilled,
              IsVirtualized: virtualized,
              TotalRows: gridView.countRows,
              TotalServerSideRows: gridView.totalCountRows,
              // For the visible rows, do not count the tr.top-filler and tr.bottom-filler
              TotalVisibleRows: gridView.$('tbody > tr:visible').length - 2,
              TotalColumns: gridView.$('table').last().find('colgroup > col').length,
              DataFetchStartTime: timeStarted,
              DataFetchStartActiveTime: activeTimeBeforeStarted,
              DataFetchingTime: timeFetched - timeStarted,
              DataFetchingActiveTime: activeTimeBeforeFetched - activeTimeBeforeStarted,
              GridRenderingTime: timeRendered - timeFetched,
              GridRenderingActiveTime: activeTimeBeforeRendered - activeTimeBeforeFetched,
            }, logNavTiming ? { // Add navigation timing info to indicate this is first Grid load
              PageNavStartTime: performance.timing.navigationStart,
              PageFetchStartTime: performance.timing.fetchStart,
              LandingPageUrl: landingPageUrl,
            } : {})),
            logger,
          });
        }
      });
    }
  });
}

export const performanceLogPlugin = definePlugin => definePlugin('performance-log', [
  'config',
  'gridView',
], ({
  scrolling: {
    virtualized = false,
  } = {},
  name,
  plugins,
}, gridView) => {
  const track = {};
  const { appConfig, logger } = plugins.performanceLog;

  gridView.on('willReload', () => {
    track.id = _.uniqueId('log-track-');

    logPerformanceInfo({
      name,
      virtualized,
      gridView,
      logNavTiming: isInitialGridDataLoad,
      track,
      appConfig,
      logger,
    });

    isInitialGridDataLoad = false;
  });
});
